import React, { Suspense } from 'react';
import { CardSkeleton, Modal } from '../../ui/common';

// Lazy: each of these widgets (directly or transitively via NotesWidget /
// DailyWidget → RichTextComposer) pulls in TipTap. ToolDrawerModal already
// early-returns when closed, so lazy-loading its three tools moves ~30 kB
// of their own code out of the entry chunk until the drawer is opened.
// TipTap itself is NOT fully out of the entry chunk yet: SlackCardExpanded
// and SettingsSlackSection still deep-import RichTextComposer eagerly (see
// proposal.md's Deferred section).
const DailyWidget = React.lazy(() =>
  import('../daily/DailyWidget').then((m) => ({ default: m.DailyWidget })),
);
const NotesWidget = React.lazy(() =>
  import('../notes/NotesWidget').then((m) => ({ default: m.NotesWidget })),
);
const LlmWikiWidget = React.lazy(() =>
  import('../wiki/LlmWikiWidget').then((m) => ({ default: m.LlmWikiWidget })),
);

export type ActiveToolType = 'daily' | 'notes' | null;
export type WorkspaceToolType = 'daily' | 'notes' | 'wiki' | null;

interface ToolDrawerModalProps {
  activeTool: WorkspaceToolType;
  onClose: () => void;
  onSelectTool: (tool: Exclude<WorkspaceToolType, null>) => void;
  wikiPageId?: string | null;
}

const TOOL_KEYS = ['daily', 'notes', 'wiki'] as const;

function tabId(toolKey: Exclude<WorkspaceToolType, null>): string {
  return `tool-drawer-tab-${toolKey}`;
}

function panelId(toolKey: Exclude<WorkspaceToolType, null>): string {
  return `tool-drawer-panel-${toolKey}`;
}

export const ToolDrawerModal: React.FC<ToolDrawerModalProps> = ({
  activeTool,
  onClose,
  onSelectTool,
  wikiPageId = null,
}) => {
  const tabRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  if (!activeTool) return null;

  const titles: Record<Exclude<WorkspaceToolType, null>, string> = {
    daily: 'Daily Standup Assistant',
    notes: 'Notas Rápidas & Contexto',
    wiki: 'LLM Wiki',
  };
  const labels: Record<Exclude<WorkspaceToolType, null>, string> = {
    daily: 'Daily Standup',
    notes: 'Notas',
    wiki: 'LLM Wiki',
  };

  const activeIndex = TOOL_KEYS.indexOf(activeTool);

  // Full WAI-ARIA tabs pattern keyboard support: ArrowLeft/ArrowRight move
  // roving focus and activate the adjacent tab (wrapping at the ends), Home/
  // End jump to the first/last tab. See
  // https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
  function handleTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    let nextIndex: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (activeIndex + 1) % TOOL_KEYS.length;
        break;
      case 'ArrowLeft':
        nextIndex = (activeIndex - 1 + TOOL_KEYS.length) % TOOL_KEYS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = TOOL_KEYS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const nextTool = TOOL_KEYS[nextIndex];
    onSelectTool(nextTool);
    tabRefs.current[nextTool]?.focus();
  }

  return (
    <Modal
      isOpen={activeTool !== null}
      onClose={onClose}
      title={titles[activeTool]}
      ariaLabel={titles[activeTool]}
      size="xl"
      width={1180}
      fullscreenOnMobile
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Sticky Segmented Tabs */}
        <div className="z-modal-sticky-tabs">
          <div role="tablist" className="z-segmented-tabs" aria-label="Herramientas del workspace">
            {TOOL_KEYS.map((toolKey) => {
              const active = activeTool === toolKey;
              return (
                <button
                  key={toolKey}
                  ref={(el) => {
                    tabRefs.current[toolKey] = el;
                  }}
                  type="button"
                  role="tab"
                  id={tabId(toolKey)}
                  aria-selected={active}
                  aria-controls={panelId(toolKey)}
                  tabIndex={active ? 0 : -1}
                  onClick={() => onSelectTool(toolKey)}
                  onKeyDown={handleTabKeyDown}
                  className={`z-segmented-tab-btn ${active ? 'z-segmented-tab-btn--active' : ''}`}
                >
                  {labels[toolKey]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Render Selected Tool Widget */}
        <div
          role="tabpanel"
          id={panelId(activeTool)}
          aria-labelledby={tabId(activeTool)}
          tabIndex={0}
          style={{ marginTop: 2, minWidth: 0 }}
        >
          <Suspense fallback={<CardSkeleton count={2} height={90} />}>
            {activeTool === 'daily' && <DailyWidget />}
            {activeTool === 'notes' && <NotesWidget />}
            {activeTool === 'wiki' && <LlmWikiWidget initialPageId={wikiPageId} />}
          </Suspense>
        </div>
      </div>
    </Modal>
  );
};
